<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    UserOutlined,
    ShoppingOutlined,
    TrophyOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    FileTextOutlined
} from '@ant-design/icons-vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    getUserStats,
    getRankDistribution,
    getOrderStats,
    getCancellationStats,
    getTopSelling,
    getMissionReviewStats,
    getMissionActivity
} from '@/api/dashboard';
import { fetchAllRankRows } from '@/api/ranks';
import {
    formatNumber,
    formatCurrency,
    formatPercentage
} from '@/utils/formatNumber';
import formatDateTime from '@/utils/formatDateTime';

const adminStore = useAdminInfo();
const router = useRouter();
const username = ref('');
const loading = ref(false);

// 權限檢查（控制台統計卡片）
const canViewMemberStats = computed(() =>
    adminStore.hasPermissionCode('160008')
); // 會員統計
const canViewRankDistribution = computed(() =>
    adminStore.hasPermissionCode('160001')
); // 會員聘階分布
const canViewOrderStats = computed(() =>
    adminStore.hasPermissionCode('160002')
); // 訂單統計
const canViewCancellationStats = computed(() =>
    adminStore.hasPermissionCode('160004')
); // 取消申請統計
const canViewTopSelling = computed(() =>
    adminStore.hasPermissionCode('160005')
); // 熱銷商品統計
const canViewMissionReviewStats = computed(() =>
    adminStore.hasPermissionCode('160006')
); // 任務需審核統計
const canViewMissionActivity = computed(() =>
    adminStore.hasPermissionCode('160007')
); // 任務執行活動

// 「查看全部」按鈕對應的目標頁權限（有權限才顯示按鈕）
const canAccessMemberList = computed(() =>
    adminStore.hasPermissionCode('100000')
); // 會員身份分布
const canAccessRanks = computed(() => adminStore.hasPermissionCode('900200')); // 會員聘階分布
const canAccessOrdersList = computed(() =>
    adminStore.hasPermissionCode('150300')
); // 熱銷商品 / 訂單
const canAccessOrderCancellations = computed(() =>
    adminStore.hasPermissionCode('150300')
); // 待審核取消
const canAccessMissionRecords = computed(() =>
    adminStore.hasPermissionCode('130200')
); // 任務活動統計
const canAccessMissionReviews = computed(() =>
    adminStore.hasPermissionCode('130300')
); // 待審核任務

// 統計卡片數據
const statsData = ref([]);

// 會員統計
const userStats = ref(null);

// 會員聘階分布
const rankDistribution = ref([]);
const totalUsers = ref(0);
// 聘階顏色對應表（從 ranks API 取得）
const rankColorMap = ref({});

// 訂單統計
const orderStats = ref(null);

// 取消申請統計
const cancellationStats = ref(null);

// 熱銷商品
const topSelling = ref([]);

// 任務審核統計
const missionReviewStats = ref(null);

// 任務活動（階層式資料）
const missionActivity = ref([]);
// 控制各層級的展開狀態
const expandedCategoryKeys = ref([]);
const expandedMissionKeys = ref([]);
const expandedChapterKeys = ref([]);
const expandedTaskKeys = ref([]);

// 獲取所有統計資料（根據權限）
const fetchAllStats = async () => {
    loading.value = true;
    try {
        const promises = [];

        // 根據權限決定要呼叫哪些 API
        if (canViewRankDistribution.value || canViewMemberStats.value) {
            promises.push(fetchRanks()); // 先取得聘階顏色資訊
        }
        if (canViewMemberStats.value) {
            promises.push(fetchUserStats());
        }
        if (canViewRankDistribution.value) {
            promises.push(fetchRankDistribution());
        }
        if (canViewOrderStats.value) {
            promises.push(fetchOrderStats());
        }
        if (canViewCancellationStats.value) {
            promises.push(fetchCancellationStats());
        }
        if (canViewTopSelling.value) {
            promises.push(fetchTopSelling());
        }
        if (canViewMissionReviewStats.value) {
            promises.push(fetchMissionReviewStats());
        }
        if (canViewMissionActivity.value) {
            promises.push(fetchMissionActivity());
        }

        await Promise.all(promises);

        // 合併顏色資訊到 rankDistribution
        if (canViewRankDistribution.value) {
            mergeRankColors();
        }
        updateStatsCards();
    } catch (error) {
        console.error('獲取統計資料失敗:', error);
        showErrorMessage('獲取統計資料失敗');
    } finally {
        loading.value = false;
    }
};

// 獲取聘階列表（用於取得顏色資訊）
const fetchRanks = async () => {
    try {
        const data = await fetchAllRankRows();
        const colorMap = {};
        data.forEach((rank) => {
            if (rank.id && rank.color) {
                colorMap[rank.id] = rank.color;
            }
        });
        rankColorMap.value = colorMap;
    } catch (error) {
        console.error('獲取聘階列表失敗:', error);
    }
};

// 合併顏色資訊到 rankDistribution
const mergeRankColors = () => {
    rankDistribution.value = rankDistribution.value.map((rank) => ({
        ...rank,
        color: rankColorMap.value[rank.rank_id] || '#FFFFFF' // 如果沒有顏色，使用白色
    }));
};

// 獲取會員統計
const fetchUserStats = async () => {
    try {
        const response = await getUserStats();
        if (response.result && response.data) {
            userStats.value = response.data;
            // 如果聘階分布沒有總會員數，使用會員統計的總數
            if (!totalUsers.value && response.data.total_users) {
                totalUsers.value = response.data.total_users;
            }
        }
    } catch (error) {
        console.error('獲取會員統計失敗:', error);
    }
};

// 獲取會員聘階分布
const fetchRankDistribution = async () => {
    try {
        const response = await getRankDistribution();
        if (response.result && response.data) {
            rankDistribution.value = response.data.distribution || [];
            // 如果會員統計沒有總數，使用聘階分布的總數
            if (!userStats.value?.total_users && response.data.total_users) {
                totalUsers.value = response.data.total_users;
            } else if (response.data.total_users) {
                totalUsers.value = response.data.total_users;
            }
        }
    } catch (error) {
        console.error('獲取聘階分布失敗:', error);
    }
};

// 獲取訂單統計
const fetchOrderStats = async () => {
    try {
        const response = await getOrderStats();
        if (response.result && response.data) {
            orderStats.value = response.data;
        }
    } catch (error) {
        console.error('獲取訂單統計失敗:', error);
    }
};

// 獲取取消申請統計
const fetchCancellationStats = async () => {
    try {
        const response = await getCancellationStats();
        if (response.result && response.data) {
            cancellationStats.value = response.data;
        }
    } catch (error) {
        console.error('獲取取消申請統計失敗:', error);
    }
};

// 獲取熱銷商品（顯示前 5 筆）
const fetchTopSelling = async () => {
    try {
        const response = await getTopSelling({ limit: 5, days: 30 });
        if (response.result && response.data) {
            topSelling.value = response.data || [];
        }
    } catch (error) {
        console.error('獲取熱銷商品失敗:', error);
    }
};

// 獲取任務審核統計
const fetchMissionReviewStats = async () => {
    try {
        const response = await getMissionReviewStats();
        if (response.result && response.data) {
            missionReviewStats.value = response.data;
        }
    } catch (error) {
        console.error('獲取任務審核統計失敗:', error);
    }
};

// 獲取任務活動（階層式統計資料）
const fetchMissionActivity = async () => {
    try {
        const response = await getMissionActivity();
        if (response.result && response.data) {
            missionActivity.value = response.data || [];
            // 預設展開第一層（category）
            expandedCategoryKeys.value = response.data.map(
                (cat, index) => `category-${index}`
            );
        }
    } catch (error) {
        console.error('獲取任務活動失敗:', error);
    }
};

// 統計卡片分組
const orderStatsCards = ref([]);
const cancellationStatsCards = ref([]);
const missionReviewStatsCards = ref([]);

// 更新統計卡片
const updateStatsCards = () => {
    // 訂單相關統計
    const orderCards = [];
    if (orderStats.value) {
        orderCards.push({
            title: '總訂單數',
            value: formatNumber(orderStats.value.total_orders || 0),
            icon: ShoppingOutlined,
            color: 'sky-blue',
            link: '/products/orders-list'
        });

        orderCards.push({
            title: '待處理訂單',
            value: formatNumber(orderStats.value.pending_orders || 0),
            icon: ClockCircleOutlined,
            color: 'sunset-orange',
            link: '/products/orders-list?status=pending'
        });

        orderCards.push({
            title: '總營收',
            value: formatCurrency(orderStats.value.total_revenue || 0),
            icon: DollarOutlined,
            color: 'neon-purple',
            link: '/products/orders-list'
        });
    }
    orderStatsCards.value = orderCards;

    // 取消申請統計
    const cancellationCards = [];
    if (cancellationStats.value) {
        cancellationCards.push({
            title: '待審核取消',
            value: formatNumber(cancellationStats.value.pending || 0),
            icon: FileTextOutlined,
            color: 'neon-purple',
            link: '/products/order-cancellations'
        });
    }
    cancellationStatsCards.value = cancellationCards;

    // 任務審核統計
    const missionReviewCards = [];
    if (missionReviewStats.value) {
        missionReviewCards.push({
            title: '待審核任務',
            value: formatNumber(missionReviewStats.value.pending_count || 0),
            icon: TrophyOutlined,
            color: 'sunset-orange',
            link: '/missions/mission-reviews'
        });
    }
    missionReviewStatsCards.value = missionReviewCards;
};

// 處理卡片點擊（支援查詢參數，並檢查目標頁權限）
const handleCardClick = (link) => {
    if (!link) return;
    // 依目標路徑檢查是否有權限，無權限則不跳轉
    if (
        link.includes('/products/order-cancellations') &&
        !canAccessOrderCancellations.value
    )
        return;
    if (
        link.includes('/missions/mission-reviews') &&
        !canAccessMissionReviews.value
    )
        return;
    if (link.includes('/products/orders-list') && !canAccessOrdersList.value)
        return;
    if (link.includes('?')) {
        router.push(link);
    } else {
        router.push(link);
    }
};

// 格式化時間差（相對時間）
const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins}分鐘前`;
    if (diffHours < 24) return `${diffHours}小時前`;
    if (diffDays < 7) return `${diffDays}天前`;
    return formatDateTime(dateString);
};

// 生成唯一 key
const getCategoryKey = (index) => `category-${index}`;
const getMissionKey = (categoryIndex, missionIndex) =>
    `mission-${categoryIndex}-${missionIndex}`;
const getChapterKey = (categoryIndex, missionIndex, chapterIndex) =>
    `chapter-${categoryIndex}-${missionIndex}-${chapterIndex}`;
const getTaskKey = (categoryIndex, missionIndex, chapterIndex, taskIndex) =>
    `task-${categoryIndex}-${missionIndex}-${chapterIndex}-${taskIndex}`;

onMounted(async () => {
    // 獲取管理員資訊
    const adminInfo = adminStore.getAdminInfo();
    username.value = adminInfo.name || '管理員';

    // 獲取所有統計資料
    await fetchAllStats();
});
</script>

<template>
    <div class="vaporwave-dashboard">
        <!-- 歡迎標題 -->
        <div class="welcome-section">
            <h1 class="welcome-title">歡迎回來，{{ username }}！</h1>
            <p class="welcome-subtitle">即時掌握系統運行概況與營運關鍵指標</p>
        </div>

        <!-- 會員相關統計 -->
        <a-spin :spinning="loading">
            <!-- 會員身份分布 -->
            <div
                v-if="
                    canViewMemberStats &&
                    userStats &&
                    userStats.by_identity &&
                    userStats.by_identity.length > 0
                "
                class="member-section"
            >
                <div class="vaporwave-card">
                    <div class="card-header">
                        <div class="card-title-wrapper">
                            <h2 class="card-title">會員身份分布</h2>
                            <span
                                v-if="userStats.total_users"
                                class="card-subtitle"
                            >
                                總計:
                                {{ formatNumber(userStats.total_users) }} 人
                            </span>
                        </div>
                        <a-button
                            v-if="canAccessMemberList"
                            type="link"
                            class="vaporwave-link-btn"
                            @click="router.push({ name: 'MembersList' })"
                        >
                            查看全部
                        </a-button>
                    </div>
                    <div class="identity-horizontal-grid">
                        <button
                            v-if="canAccessMemberList"
                            type="button"
                            class="identity-item-horizontal identity-item-button identity-item-line-unbound"
                            @click="
                                router.push({
                                    name: 'MembersGeneralAdmin',
                                    query: { has_line: '0' }
                                })
                            "
                        >
                            <div class="identity-name-horizontal">
                                未綁定 LINE
                            </div>
                            <div class="identity-count-horizontal">
                                {{
                                    formatNumber(
                                        userStats.line_unbound_users || 0
                                    )
                                }}
                                人
                            </div>
                            <div class="identity-percentage-horizontal">
                                點擊查看名單
                            </div>
                        </button>
                        <div
                            v-for="identity in userStats.by_identity"
                            :key="identity.identity"
                            class="identity-item-horizontal"
                        >
                            <div class="identity-name-horizontal">
                                {{ identity.identity_name }}
                            </div>
                            <div class="identity-count-horizontal">
                                {{ formatNumber(identity.count) }} 人
                            </div>
                            <div class="identity-percentage-horizontal">
                                {{ formatPercentage(identity.percentage) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 會員聘階分布 -->
            <div
                v-if="canViewRankDistribution && rankDistribution.length > 0"
                class="member-section"
            >
                <div class="vaporwave-card">
                    <div class="card-header">
                        <div class="card-title-wrapper">
                            <h2 class="card-title">會員聘階分布</h2>
                            <span v-if="totalUsers > 0" class="card-subtitle">
                                總計: {{ formatNumber(totalUsers) }} 人
                            </span>
                        </div>
                        <a-button
                            v-if="canAccessRanks"
                            type="link"
                            class="vaporwave-link-btn"
                            @click="router.push('/partner-ops/ranks')"
                        >
                            查看全部
                        </a-button>
                    </div>
                    <div class="rank-horizontal-grid">
                        <div
                            v-for="rank in rankDistribution"
                            :key="rank.rank_id"
                            class="rank-item-horizontal"
                        >
                            <div class="rank-header-horizontal">
                                <div
                                    class="rank-color-badge-horizontal"
                                    :style="{
                                        backgroundColor: rank.color || '#FFFFFF'
                                    }"
                                ></div>
                                <div class="rank-name-horizontal">
                                    {{ rank.rank_name }}
                                </div>
                            </div>
                            <div class="rank-count-horizontal">
                                {{ formatNumber(rank.user_count) }} 人
                            </div>
                            <div class="rank-percentage-horizontal">
                                {{ formatPercentage(rank.percentage) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a-spin>

        <!-- 訂單統計與熱銷商品 -->
        <a-spin :spinning="loading">
            <div class="order-products-layout">
                <!-- 左側：訂單統計 -->
                <div class="order-stats-left">
                    <div
                        v-if="
                            (canViewOrderStats || canViewCancellationStats) &&
                            (orderStatsCards.length > 0 ||
                                cancellationStatsCards.length > 0)
                        "
                        class="stats-section"
                    >
                        <h2 class="section-title">訂單統計</h2>
                        <div class="stats-grid-custom">
                            <!-- 第一行：總訂單數（佔兩列）- 需要訂單統計權限 -->
                            <template
                                v-for="stat in orderStatsCards"
                                :key="stat.title"
                            >
                                <div
                                    v-if="
                                        canViewOrderStats &&
                                        stat.title === '總訂單數'
                                    "
                                    class="vaporwave-stat-card stat-card-span-2"
                                    @click="
                                        stat.link && handleCardClick(stat.link)
                                    "
                                >
                                    <div class="stat-content">
                                        <div class="stat-info">
                                            <p class="stat-title">
                                                {{ stat.title }}
                                            </p>
                                            <p class="stat-value">
                                                {{ stat.value }}
                                            </p>
                                        </div>
                                        <div
                                            class="stat-icon-wrapper"
                                            :class="`icon-${stat.color}`"
                                        >
                                            <component
                                                :is="stat.icon"
                                                class="stat-icon"
                                            />
                                        </div>
                                    </div>
                                    <div class="card-glow"></div>
                                </div>
                            </template>
                            <!-- 第二行：待處理訂單和待審核取消 -->
                            <template
                                v-for="stat in orderStatsCards"
                                :key="stat.title"
                            >
                                <div
                                    v-if="
                                        canViewOrderStats &&
                                        stat.title === '待處理訂單'
                                    "
                                    class="vaporwave-stat-card"
                                    @click="
                                        stat.link && handleCardClick(stat.link)
                                    "
                                >
                                    <div class="stat-content">
                                        <div class="stat-info">
                                            <p class="stat-title">
                                                {{ stat.title }}
                                            </p>
                                            <p class="stat-value">
                                                {{ stat.value }}
                                            </p>
                                        </div>
                                        <div
                                            class="stat-icon-wrapper"
                                            :class="`icon-${stat.color}`"
                                        >
                                            <component
                                                :is="stat.icon"
                                                class="stat-icon"
                                            />
                                        </div>
                                    </div>
                                    <div class="card-glow"></div>
                                </div>
                            </template>
                            <!-- 待審核取消 - 需要取消申請統計權限 -->
                            <template
                                v-for="stat in cancellationStatsCards"
                                :key="stat.title"
                            >
                                <div
                                    v-if="canViewCancellationStats"
                                    class="vaporwave-stat-card"
                                    @click="
                                        stat.link && handleCardClick(stat.link)
                                    "
                                >
                                    <div class="stat-content">
                                        <div class="stat-info">
                                            <p class="stat-title">
                                                {{ stat.title }}
                                            </p>
                                            <p class="stat-value">
                                                {{ stat.value }}
                                            </p>
                                        </div>
                                        <div
                                            class="stat-icon-wrapper"
                                            :class="`icon-${stat.color}`"
                                        >
                                            <component
                                                :is="stat.icon"
                                                class="stat-icon"
                                            />
                                        </div>
                                    </div>
                                    <div class="card-glow"></div>
                                </div>
                            </template>
                            <!-- 第三行：總營收（佔兩列）- 需要訂單統計權限 -->
                            <template
                                v-for="stat in orderStatsCards"
                                :key="stat.title"
                            >
                                <div
                                    v-if="
                                        canViewOrderStats &&
                                        stat.title === '總營收'
                                    "
                                    class="vaporwave-stat-card stat-card-span-2"
                                    @click="
                                        stat.link && handleCardClick(stat.link)
                                    "
                                >
                                    <div class="stat-content">
                                        <div class="stat-info">
                                            <p class="stat-title">
                                                {{ stat.title }}
                                            </p>
                                            <p class="stat-value">
                                                {{ stat.value }}
                                            </p>
                                        </div>
                                        <div
                                            class="stat-icon-wrapper"
                                            :class="`icon-${stat.color}`"
                                        >
                                            <component
                                                :is="stat.icon"
                                                class="stat-icon"
                                            />
                                        </div>
                                    </div>
                                    <div class="card-glow"></div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- 右側：熱銷商品 -->
                <div v-if="canViewTopSelling" class="products-section-right">
                    <div class="vaporwave-card">
                        <div class="card-header">
                            <h2 class="card-title">熱銷商品</h2>
                            <a-button
                                v-if="canAccessOrdersList"
                                type="link"
                                class="vaporwave-link-btn"
                                @click="router.push('/products/orders-list')"
                            >
                                查看全部
                            </a-button>
                        </div>
                        <div v-if="topSelling.length === 0" class="empty-state">
                            <p>暫無熱銷商品</p>
                        </div>
                        <div v-else class="products-list">
                            <div
                                v-for="product in topSelling"
                                :key="product.id"
                                class="product-item"
                            >
                                <div class="product-info">
                                    <p class="product-name">
                                        {{ product.name }}
                                    </p>
                                    <p class="product-sales">
                                        銷量:
                                        {{ formatNumber(product.total_sold) }}
                                    </p>
                                </div>
                                <div class="product-stats">
                                    <p class="product-revenue">
                                        {{
                                            formatCurrency(
                                                product.total_revenue
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a-spin>

        <!-- 任務審核統計 -->
        <a-spin :spinning="loading">
            <div
                v-if="
                    canViewMissionReviewStats &&
                    missionReviewStatsCards.length > 0
                "
                class="stats-section"
            >
                <h2 class="section-title">任務審核統計</h2>
                <div class="stats-grid">
                    <div
                        v-for="stat in missionReviewStatsCards"
                        :key="stat.title"
                        class="vaporwave-stat-card"
                        @click="stat.link && handleCardClick(stat.link)"
                    >
                        <div class="stat-content">
                            <div class="stat-info">
                                <p class="stat-title">{{ stat.title }}</p>
                                <p class="stat-value">
                                    {{ stat.value }}
                                </p>
                            </div>
                            <div
                                class="stat-icon-wrapper"
                                :class="`icon-${stat.color}`"
                            >
                                <component :is="stat.icon" class="stat-icon" />
                            </div>
                        </div>
                        <div class="card-glow"></div>
                    </div>
                </div>
            </div>
        </a-spin>

        <!-- 任務活動統計 -->
        <a-spin :spinning="loading">
            <div v-if="canViewMissionActivity" class="activity-section">
                <div class="vaporwave-card">
                    <div class="card-header">
                        <h2 class="card-title">任務活動統計</h2>
                        <a-button
                            v-if="canAccessMissionRecords"
                            type="link"
                            class="vaporwave-link-btn"
                            @click="router.push('/missions/mission-records')"
                        >
                            查看全部
                        </a-button>
                    </div>
                    <div
                        v-if="missionActivity.length === 0"
                        class="empty-state"
                    >
                        <p>暫無任務活動資料</p>
                    </div>
                    <div v-else class="mission-activity-hierarchy">
                        <a-collapse
                            v-model:activeKey="expandedCategoryKeys"
                            :bordered="false"
                            class="mission-category-collapse"
                        >
                            <a-collapse-panel
                                v-for="(category, catIndex) in missionActivity"
                                :key="getCategoryKey(catIndex)"
                                class="category-panel"
                            >
                                <template #header>
                                    <div class="category-header">
                                        <div class="category-title-wrapper">
                                            <TrophyOutlined
                                                class="category-icon"
                                            />
                                            <span class="category-name">
                                                {{ category.category_name }}
                                            </span>
                                        </div>
                                        <div class="category-stats">
                                            <span class="stat-item">
                                                總用戶:
                                                {{
                                                    formatNumber(
                                                        category.stats
                                                            ?.total_users || 0
                                                    )
                                                }}
                                            </span>
                                            <span class="stat-item">
                                                完成率:
                                                {{
                                                    formatPercentage(
                                                        category.stats
                                                            ?.completion_rate ||
                                                            0
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <div class="category-content">
                                    <!-- 統計摘要 -->
                                    <div class="stats-summary">
                                        <div class="summary-item">
                                            <span class="summary-label">
                                                總進度
                                            </span>
                                            <span class="summary-value">
                                                {{
                                                    formatNumber(
                                                        category.stats
                                                            ?.total_progress ||
                                                            0
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="summary-item">
                                            <span class="summary-label">
                                                已完成
                                            </span>
                                            <span
                                                class="summary-value completed"
                                            >
                                                {{
                                                    formatNumber(
                                                        category.stats
                                                            ?.completed || 0
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="summary-item">
                                            <span class="summary-label">
                                                進行中
                                            </span>
                                            <span
                                                class="summary-value in-progress"
                                            >
                                                {{
                                                    formatNumber(
                                                        category.stats
                                                            ?.in_progress || 0
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="summary-item">
                                            <span class="summary-label">
                                                失敗
                                            </span>
                                            <span class="summary-value failed">
                                                {{
                                                    formatNumber(
                                                        category.stats
                                                            ?.failed || 0
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="summary-item">
                                            <span class="summary-label">
                                                已取消
                                            </span>
                                            <span
                                                class="summary-value cancelled"
                                            >
                                                {{
                                                    formatNumber(
                                                        category.stats
                                                            ?.cancelled || 0
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <!-- 任務列表 -->
                                    <a-collapse
                                        v-model:activeKey="expandedMissionKeys"
                                        :bordered="false"
                                        class="mission-collapse"
                                    >
                                        <a-collapse-panel
                                            v-for="(
                                                mission, missionIndex
                                            ) in category.missions || []"
                                            :key="
                                                getMissionKey(
                                                    catIndex,
                                                    missionIndex
                                                )
                                            "
                                            class="mission-panel"
                                        >
                                            <template #header>
                                                <div class="mission-header">
                                                    <span class="mission-name">
                                                        {{ mission.name }}
                                                    </span>
                                                    <div
                                                        class="mission-stats-inline"
                                                    >
                                                        <span
                                                            class="stat-badge completed"
                                                        >
                                                            {{
                                                                formatNumber(
                                                                    mission
                                                                        .stats
                                                                        ?.completed ||
                                                                        0
                                                                )
                                                            }}
                                                            完成
                                                        </span>
                                                        <span
                                                            class="stat-badge in-progress"
                                                        >
                                                            {{
                                                                formatNumber(
                                                                    mission
                                                                        .stats
                                                                        ?.in_progress ||
                                                                        0
                                                                )
                                                            }}
                                                            進行中
                                                        </span>
                                                    </div>
                                                </div>
                                            </template>
                                            <div class="mission-content">
                                                <a-collapse
                                                    v-model:activeKey="
                                                        expandedChapterKeys
                                                    "
                                                    :bordered="false"
                                                    class="chapter-collapse"
                                                >
                                                    <a-collapse-panel
                                                        v-for="(
                                                            chapter,
                                                            chapterIndex
                                                        ) in mission.chapters ||
                                                        []"
                                                        :key="
                                                            getChapterKey(
                                                                catIndex,
                                                                missionIndex,
                                                                chapterIndex
                                                            )
                                                        "
                                                        class="chapter-panel"
                                                    >
                                                        <template #header>
                                                            <div
                                                                class="chapter-header"
                                                            >
                                                                <span
                                                                    class="chapter-name"
                                                                >
                                                                    {{
                                                                        chapter.name
                                                                    }}
                                                                </span>
                                                                <div
                                                                    class="chapter-stats-inline"
                                                                >
                                                                    <span
                                                                        class="stat-badge completed"
                                                                    >
                                                                        {{
                                                                            formatNumber(
                                                                                chapter
                                                                                    .stats
                                                                                    ?.completed ||
                                                                                    0
                                                                            )
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        class="stat-badge in-progress"
                                                                    >
                                                                        {{
                                                                            formatNumber(
                                                                                chapter
                                                                                    .stats
                                                                                    ?.in_progress ||
                                                                                    0
                                                                            )
                                                                        }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </template>
                                                        <div
                                                            class="chapter-content"
                                                        >
                                                            <a-collapse
                                                                v-model:activeKey="
                                                                    expandedTaskKeys
                                                                "
                                                                :bordered="
                                                                    false
                                                                "
                                                                class="task-collapse"
                                                            >
                                                                <a-collapse-panel
                                                                    v-for="(
                                                                        task,
                                                                        taskIndex
                                                                    ) in chapter.tasks ||
                                                                    []"
                                                                    :key="
                                                                        getTaskKey(
                                                                            catIndex,
                                                                            missionIndex,
                                                                            chapterIndex,
                                                                            taskIndex
                                                                        )
                                                                    "
                                                                    class="task-panel"
                                                                >
                                                                    <template
                                                                        #header
                                                                    >
                                                                        <div
                                                                            class="task-header"
                                                                        >
                                                                            <span
                                                                                class="task-name"
                                                                            >
                                                                                {{
                                                                                    task.name
                                                                                }}
                                                                            </span>
                                                                            <div
                                                                                class="task-stats-inline"
                                                                            >
                                                                                <span
                                                                                    class="stat-badge completed"
                                                                                >
                                                                                    {{
                                                                                        formatNumber(
                                                                                            task
                                                                                                .stats
                                                                                                ?.completed ||
                                                                                                0
                                                                                        )
                                                                                    }}
                                                                                </span>
                                                                                <span
                                                                                    class="stat-badge in-progress"
                                                                                >
                                                                                    {{
                                                                                        formatNumber(
                                                                                            task
                                                                                                .stats
                                                                                                ?.in_progress ||
                                                                                                0
                                                                                        )
                                                                                    }}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </template>
                                                                    <div
                                                                        class="task-content"
                                                                    >
                                                                        <div
                                                                            v-if="
                                                                                task.steps &&
                                                                                task
                                                                                    .steps
                                                                                    .length >
                                                                                    0
                                                                            "
                                                                            class="steps-list"
                                                                        >
                                                                            <div
                                                                                v-for="(
                                                                                    step,
                                                                                    stepIndex
                                                                                ) in task.steps"
                                                                                :key="
                                                                                    stepIndex
                                                                                "
                                                                                class="step-item"
                                                                            >
                                                                                <div
                                                                                    class="step-info"
                                                                                >
                                                                                    <span
                                                                                        class="step-name"
                                                                                    >
                                                                                        {{
                                                                                            step.name
                                                                                        }}
                                                                                    </span>
                                                                                    <div
                                                                                        class="step-stats"
                                                                                    >
                                                                                        <span
                                                                                            class="stat-badge-small completed"
                                                                                        >
                                                                                            {{
                                                                                                formatNumber(
                                                                                                    step
                                                                                                        .stats
                                                                                                        ?.completed ||
                                                                                                        0
                                                                                                )
                                                                                            }}
                                                                                            完成
                                                                                        </span>
                                                                                        <span
                                                                                            class="stat-badge-small in-progress"
                                                                                        >
                                                                                            {{
                                                                                                formatNumber(
                                                                                                    step
                                                                                                        .stats
                                                                                                        ?.in_progress ||
                                                                                                        0
                                                                                                )
                                                                                            }}
                                                                                            進行中
                                                                                        </span>
                                                                                        <span
                                                                                            class="stat-badge-small failed"
                                                                                        >
                                                                                            {{
                                                                                                formatNumber(
                                                                                                    step
                                                                                                        .stats
                                                                                                        ?.failed ||
                                                                                                        0
                                                                                                )
                                                                                            }}
                                                                                            失敗
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            v-else
                                                                            class="empty-steps"
                                                                        >
                                                                            <p>
                                                                                暫無步驟資料
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </a-collapse-panel>
                                                            </a-collapse>
                                                        </div>
                                                    </a-collapse-panel>
                                                </a-collapse>
                                            </div>
                                        </a-collapse-panel>
                                    </a-collapse>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>
                    </div>
                </div>
            </div>
        </a-spin>
    </div>
</template>

<style scoped>
/* 蒸氣波風格 Dashboard */
.vaporwave-dashboard {
    min-height: 100vh;
    background: transparent;
    font-family: 'Courier New', monospace;
}

/* 歡迎區域 */
.welcome-section {
    margin-bottom: 2rem;
}

.welcome-title {
    font-size: 2rem;
    font-weight: bold;
    color: #2a2a3a;
    margin: 0;
    letter-spacing: 0.1em;
}

.welcome-subtitle {
    color: #5a4a7a;
    margin-top: 0.5rem;
    font-size: 1rem;
    opacity: 1;
    font-weight: bold;
}

/* 統計區塊 */
.stats-section {
    margin-bottom: 2.5rem;
}

.section-title {
    font-size: 1.3rem;
    font-weight: bold;
    color: #2a2a3a;
    margin: 0 0 1rem 0;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid rgba(42, 74, 122, 0.2);
    letter-spacing: 0.05em;
}

/* 統計卡片網格 */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
}

/* 自定義統計卡片網格 - 支援跨列 */
.stats-grid-custom {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
}

.stat-card-span-2 {
    grid-column: span 2;
}

@media (max-width: 768px) {
    .stats-grid-custom {
        grid-template-columns: 1fr;
    }

    .stat-card-span-2 {
        grid-column: span 1;
    }
}

/* 統計卡片 */
.vaporwave-stat-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    padding: 1.5rem;
    position: relative;
    border: 2px solid transparent;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow:
        0 4px 15px rgba(42, 74, 122, 0.12),
        inset 0 0 20px rgba(42, 74, 122, 0.03);
}

.vaporwave-stat-card:hover {
    transform: translateY(-5px);
    border-color: var(--aqua-cyan);
    box-shadow:
        0 8px 25px rgba(168, 255, 246, 0.25),
        0 4px 20px rgba(42, 74, 122, 0.15);
}

.stat-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 2;
}

.stat-info {
    flex: 1;
}

.stat-title {
    color: #6a6a7a;
    font-weight: 500;
    font-size: 0.9rem;
    margin: 0 0 0.5rem 0;
    text-transform: uppercase;
    letter-spacing: 0.1em;
}

.stat-value {
    font-size: 2rem;
    font-weight: bold;
    color: #2a2a3a;
    margin: 0 0 0.5rem 0;
}

.stat-change {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.change-value {
    font-size: 0.9rem;
    font-weight: bold;
}

.change-label {
    color: rgba(42, 42, 58, 0.8);
    font-size: 0.8rem;
}

.stat-icon-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
}

.stat-icon-wrapper::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1;
}

.stat-icon {
    font-size: 1.5rem;
    color: #ffffff;
    z-index: 2;
    position: relative;
}

/* 圖標顏色 */
.icon-sunset-pink {
    background: linear-gradient(45deg, #64748b, #e885b5);
}

.icon-aqua-cyan {
    background: linear-gradient(45deg, #0e7490, #8ce8da);
}

.icon-neon-purple {
    background: linear-gradient(45deg, #2a4a7a, #ad62e8);
}

.icon-sky-blue {
    background: linear-gradient(45deg, #3b6ea5, #6f98f0);
}

.icon-sunset-orange {
    background: linear-gradient(45deg, #ffa87a, #f59060);
}

.icon-mint-green {
    background: linear-gradient(45deg, #d0ffb7, #b5f595);
}

.card-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        45deg,
        transparent,
        rgba(168, 255, 246, 0.05),
        transparent
    );
    opacity: 0;
    transition: opacity 0.3s ease;
}

.vaporwave-stat-card:hover .card-glow {
    opacity: 1;
}

/* 主要內容區域 */
.main-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

@media (min-width: 1024px) {
    .main-content {
        grid-template-columns: 2fr 1fr;
    }
}

/* 卡片通用樣式 */
.vaporwave-card {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(20px);
    border-radius: 20px;
    padding: 1.5rem;
    border: 2px solid rgba(42, 74, 122, 0.2) !important;
    box-shadow:
        0 4px 15px rgba(42, 74, 122, 0.1),
        inset 0 0 20px rgba(42, 74, 122, 0.03) !important;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.card-title-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.card-title {
    font-size: 1.2rem;
    font-weight: bold;
    color: #2a2a3a;
    margin: 0;
}

.card-subtitle {
    font-size: 0.85rem;
    color: #6a6a7a;
    font-weight: 500;
}

.vaporwave-link-btn,
.vaporwave-link-btn :deep(span) {
    color: #7b5fd4 !important;
    font-weight: 600;
    text-shadow: none;
}

.vaporwave-link-btn:hover,
.vaporwave-link-btn:focus,
.vaporwave-link-btn:hover :deep(span),
.vaporwave-link-btn:focus :deep(span) {
    color: var(--neon-purple) !important;
}

/* 活動列表 */
.activity-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.activity-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: rgba(240, 240, 245, 0.5);
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    transition: all 0.3s ease;
}

.activity-item:hover {
    background: rgba(42, 74, 122, 0.1);
    border-color: #2a4a7a;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.3);
}

.activity-content {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.activity-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(45deg, #2a4a7a, #ad62e8);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    box-shadow: 0 0 12px rgba(42, 74, 122, 0.4);
}

.activity-details {
    flex: 1;
}

.activity-text {
    font-size: 0.9rem;
    color: #4a4a5a;
    margin: 0 0 0.25rem 0;
}

.user-name {
    font-weight: bold;
    color: #4a4a5a;
}

.action-text {
    color: #6a6a7a;
    margin: 0 0.25rem;
}

.target-text {
    color: #2a2a3a;
    font-weight: bold;
}

.activity-time {
    font-size: 0.8rem;
    color: #8a8a9a;
    margin: 0;
}

.activity-points {
    font-size: 0.9rem;
    font-weight: bold;
}

/* 商品列表 */
.products-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.product-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: rgba(240, 240, 245, 0.5);
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    transition: all 0.3s ease;
}

.product-item:hover {
    background: rgba(42, 74, 122, 0.1);
    border-color: #2a4a7a;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.3);
}

.product-name {
    font-size: 0.9rem;
    font-weight: bold;
    color: #2a2a3a;
    margin: 0 0 0.25rem 0;
}

.product-sales {
    font-size: 0.8rem;
    color: #6a6a7a;
    margin: 0;
}

.product-stats {
    text-align: right;
}

.product-revenue {
    font-size: 0.9rem;
    font-weight: bold;
    color: #2a2a3a;
    margin: 0 0 0.25rem 0;
}

.product-growth {
    font-size: 0.8rem;
    color: #6a6a7a;
    margin: 0;
}

/* 空狀態 */
.empty-state {
    text-align: center;
    padding: 2rem;
    color: #8a8a9a;
}

/* 會員相關區塊 */
.member-section {
    margin-bottom: 2rem;
}

/* 會員身份分布 - 左右呈現 */
.identity-horizontal-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}

.identity-item-horizontal {
    flex: 1;
    min-width: 150px;
    padding: 1rem;
    background: rgba(240, 240, 245, 0.5);
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    transition: all 0.3s ease;
    text-align: center;
}

.identity-item-button {
    appearance: none;
    font: inherit;
    cursor: pointer;
}

.identity-item-line-unbound {
    background: rgba(255, 236, 179, 0.45);
    border-color: rgba(250, 173, 20, 0.35);
}

.identity-item-horizontal.identity-item-line-unbound:hover {
    background: rgba(250, 173, 20, 0.12);
    border-color: #faad14;
    box-shadow: 0 2px 12px rgba(250, 173, 20, 0.25);
}

.identity-item-horizontal:hover {
    background: rgba(42, 74, 122, 0.1);
    border-color: #2a4a7a;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.3);
    transform: translateY(-2px);
}

.identity-name-horizontal {
    font-size: 1rem;
    font-weight: bold;
    color: #2a2a3a;
    margin-bottom: 0.5rem;
}

.identity-count-horizontal {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2a2a3a;
    margin-bottom: 0.25rem;
}

.identity-percentage-horizontal {
    font-size: 0.85rem;
    color: #6a6a7a;
    font-weight: 500;
}

/* 會員聘階分布 - 換行顯示 */
.rank-horizontal-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}

.rank-item-horizontal {
    flex: 1;
    min-width: 180px;
    padding: 1rem;
    background: rgba(240, 240, 245, 0.5);
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    transition: all 0.3s ease;
}

.rank-item-horizontal:hover {
    background: rgba(42, 74, 122, 0.1);
    border-color: #2a4a7a;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.3);
    transform: translateY(-2px);
}

.rank-header-horizontal {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}

.rank-color-badge-horizontal {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    flex-shrink: 0;
}

.rank-name-horizontal {
    font-size: 1rem;
    font-weight: bold;
    color: #2a2a3a;
    flex: 1;
}

.rank-count-horizontal {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2a2a3a;
    margin-bottom: 0.25rem;
}

.rank-percentage-horizontal {
    font-size: 0.85rem;
    color: #6a6a7a;
    font-weight: 500;
}

/* 訂單統計與熱銷商品左右佈局 */
.order-products-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

@media (min-width: 1024px) {
    .order-products-layout {
        grid-template-columns: 1fr 1fr;
    }
}

.order-stats-left {
    display: flex;
    flex-direction: column;
}

.products-section-right {
    display: flex;
    flex-direction: column;
}

/* 等級分布（保留舊樣式以防其他地方使用） */
.level-section {
    margin-top: 2rem;
}

.level-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.level-item {
    padding: 1.25rem;
    background: rgba(240, 240, 245, 0.5);
    border-radius: 15px;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.level-item:hover {
    border-color: #2a4a7a;
    background: rgba(42, 74, 122, 0.1);
    box-shadow: 0 4px 15px rgba(42, 74, 122, 0.3);
    transform: translateY(-3px);
}

.level-header {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.level-color-badge {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: 2px solid rgba(255, 255, 255, 0.5);
    box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.15),
        inset 0 1px 2px rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
}

.level-info {
    flex: 1;
    text-align: left;
}

.level-name {
    font-size: 1rem;
    font-weight: bold;
    color: #2a2a3a;
    margin-bottom: 0.25rem;
}

.level-count {
    font-size: 0.85rem;
    color: #6a6a7a;
    font-weight: 500;
}

.level-stats {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.level-percentage {
    font-size: 0.9rem;
    color: #4a4a5a;
    font-weight: 600;
    text-align: right;
}

.level-progress {
    width: 100%;
    height: 10px;
    background: rgba(200, 200, 210, 0.3);
    border-radius: 5px;
    overflow: hidden;
    position: relative;
}

.level-progress-bar {
    height: 100%;
    border-radius: 5px;
    transition: width 1s ease;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
}

/* 身份顏色 */
.identity-guest {
    background: linear-gradient(135deg, #e0e0e0, #bdbdbd);
}

.identity-student {
    background: linear-gradient(135deg, #3b6ea5, #6f98f0);
}

.identity-partner {
    background: linear-gradient(135deg, #2a4a7a, #ad62e8);
}

.identity-admin {
    background: linear-gradient(135deg, #ffa87a, #f59060);
}

/* 詳細統計區塊 */
.detailed-stats-section {
    margin-top: 2rem;
}

.detailed-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
}

.detailed-stats-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: rgba(240, 240, 245, 0.3);
    border-radius: 10px;
    border: 1px solid rgba(42, 74, 122, 0.1);
    transition: all 0.3s ease;
}

.stat-row:hover {
    background: rgba(42, 74, 122, 0.1);
    border-color: rgba(42, 74, 122, 0.3);
}

.stat-row.highlight {
    background: rgba(42, 74, 122, 0.15);
    border-color: rgba(42, 74, 122, 0.3);
    font-weight: bold;
}

.stat-row.sub-stat {
    margin-left: 1rem;
    background: rgba(240, 240, 245, 0.2);
    font-size: 0.9rem;
}

.stat-label {
    color: #6a6a7a;
    font-size: 0.9rem;
}

.stat-value-text {
    font-size: 1rem;
    font-weight: bold;
    color: #2a2a3a;
}

/* 活動狀態 */
.activity-status {
    font-size: 0.85rem;
    font-weight: bold;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    background: rgba(240, 240, 245, 0.5);
}

.activity-status-completed {
    color: #52c41a;
    background: rgba(82, 196, 26, 0.1);
    border: 1px solid rgba(82, 196, 26, 0.3);
}

.activity-status-progress {
    color: #1890ff;
    background: rgba(24, 144, 255, 0.1);
    border: 1px solid rgba(24, 144, 255, 0.3);
}

.activity-status-pending {
    color: #ff7a45;
    background: rgba(255, 122, 69, 0.1);
    border: 1px solid rgba(255, 122, 69, 0.3);
}

/* 任務活動階層式呈現 */
.mission-activity-hierarchy {
    margin-top: 1rem;
}

.mission-category-collapse {
    background: transparent;
}

.mission-category-collapse :deep(.ant-collapse-item) {
    border: none;
    margin-bottom: 1rem;
    background: rgba(240, 240, 245, 0.3);
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    overflow: hidden;
}

.mission-category-collapse :deep(.ant-collapse-header) {
    padding: 1rem 1.5rem;
    background: transparent;
}

.category-panel {
    background: transparent;
}

.category-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.category-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.category-icon {
    font-size: 1.2rem;
    color: var(--neon-purple);
}

.category-name {
    font-size: 1.1rem;
    font-weight: bold;
    color: #2a2a3a;
}

.category-stats {
    display: flex;
    gap: 1rem;
}

.stat-item {
    font-size: 0.9rem;
    color: #6a6a7a;
    font-weight: 500;
}

.category-content {
    padding: 1rem 0;
}

.stats-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 10px;
    border: 1px solid rgba(42, 74, 122, 0.15);
}

.summary-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 100px;
}

.summary-label {
    font-size: 0.85rem;
    color: #6a6a7a;
    font-weight: 500;
}

.summary-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: #2a2a3a;
}

.summary-value.completed {
    color: #52c41a;
}

.summary-value.in-progress {
    color: #1890ff;
}

.summary-value.failed {
    color: #ff4d4f;
}

.summary-value.cancelled {
    color: #8a8a9a;
}

.mission-collapse {
    background: transparent;
}

.mission-collapse :deep(.ant-collapse-item) {
    border: none;
    margin-bottom: 0.75rem;
    background: rgba(255, 255, 255, 0.4);
    border-radius: 10px;
    border: 1px solid rgba(42, 74, 122, 0.15);
}

.mission-collapse :deep(.ant-collapse-header) {
    padding: 0.75rem 1rem;
}

.mission-panel {
    background: transparent;
}

.mission-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.mission-name {
    font-size: 1rem;
    font-weight: 600;
    color: #2a2a3a;
}

.mission-stats-inline {
    display: flex;
    gap: 0.5rem;
}

.mission-content {
    padding: 0.5rem 0;
}

.chapter-collapse {
    background: transparent;
}

.chapter-collapse :deep(.ant-collapse-item) {
    border: none;
    margin-bottom: 0.5rem;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    border: 1px solid rgba(42, 74, 122, 0.1);
}

.chapter-collapse :deep(.ant-collapse-header) {
    padding: 0.5rem 0.75rem;
}

.chapter-panel {
    background: transparent;
}

.chapter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.chapter-name {
    font-size: 0.95rem;
    font-weight: 500;
    color: #2a2a3a;
}

.chapter-stats-inline {
    display: flex;
    gap: 0.5rem;
}

.chapter-content {
    padding: 0.5rem 0;
}

.task-collapse {
    background: transparent;
}

.task-collapse :deep(.ant-collapse-item) {
    border: none;
    margin-bottom: 0.5rem;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    border: 1px solid rgba(42, 74, 122, 0.08);
}

.task-collapse :deep(.ant-collapse-header) {
    padding: 0.5rem 0.75rem;
}

.task-panel {
    background: transparent;
}

.task-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.task-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: #4a4a5a;
}

.task-stats-inline {
    display: flex;
    gap: 0.5rem;
}

.task-content {
    padding: 0.5rem 0;
}

.steps-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 0.5rem;
}

.step-item {
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    border: 1px solid rgba(42, 74, 122, 0.1);
    transition: all 0.3s ease;
}

.step-item:hover {
    background: rgba(42, 74, 122, 0.1);
    border-color: rgba(42, 74, 122, 0.2);
}

.step-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.step-name {
    font-size: 0.85rem;
    color: #4a4a5a;
    font-weight: 500;
}

.step-stats {
    display: flex;
    gap: 0.5rem;
}

.stat-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
}

.stat-badge.completed {
    background: rgba(82, 196, 26, 0.15);
    color: #52c41a;
    border: 1px solid rgba(82, 196, 26, 0.3);
}

.stat-badge.in-progress {
    background: rgba(24, 144, 255, 0.15);
    color: #1890ff;
    border: 1px solid rgba(24, 144, 255, 0.3);
}

.stat-badge.failed {
    background: rgba(255, 77, 79, 0.15);
    color: #ff4d4f;
    border: 1px solid rgba(255, 77, 79, 0.3);
}

.stat-badge-small {
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
}

.stat-badge-small.completed {
    background: rgba(82, 196, 26, 0.15);
    color: #52c41a;
    border: 1px solid rgba(82, 196, 26, 0.3);
}

.stat-badge-small.in-progress {
    background: rgba(24, 144, 255, 0.15);
    color: #1890ff;
    border: 1px solid rgba(24, 144, 255, 0.3);
}

.stat-badge-small.failed {
    background: rgba(255, 77, 79, 0.15);
    color: #ff4d4f;
    border: 1px solid rgba(255, 77, 79, 0.3);
}

.empty-steps {
    padding: 1rem;
    text-align: center;
    color: #8a8a9a;
    font-size: 0.85rem;
}

/* 顏色類別 - 改善對比度 */
.text-mint-green {
    color: #52c41a;
    font-weight: 600;
}

.text-sunset-orange {
    color: #ff7a45;
    font-weight: 600;
}

.text-aqua-cyan {
    color: #13c2c2;
    font-weight: 600;
}

.text-sky-blue {
    color: #1890ff;
    font-weight: 600;
}

.text-gray-500 {
    color: #8a8a9a;
}

.text-neon-purple {
    color: #722ed1;
    font-weight: 600;
}

/* 響應式設計 */
@media (max-width: 1024px) {
    .main-content {
        grid-template-columns: 1fr;
    }

    .stats-grid {
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }

    .detailed-stats-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .vaporwave-dashboard {
        padding: 1rem;
    }

    .welcome-title {
        font-size: 1.5rem;
        margin-top: 12px;
    }

    .stats-grid {
        grid-template-columns: 1fr;
    }

    .identity-horizontal-grid {
        flex-direction: column;
    }

    .identity-item-horizontal {
        min-width: 100%;
    }

    .rank-horizontal-grid {
        flex-direction: column;
    }

    .rank-item-horizontal {
        min-width: 100%;
    }

    .level-grid {
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }

    .detailed-stats-grid {
        grid-template-columns: 1fr;
    }
}
</style>
